import { useCallback, useEffect, useMemo, useState } from "react";
import "../../styles/remediationConfig.css";
import { API_BASE } from "../../config";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import { ArrowheadDown, ArrowheadUp, Trash } from "../icons";
import MOCK_REMEDIATION_CONFIG from "./MockConfig.json";
import {
    CASE_CLOSED_TYPES,
    DEFAULT_DEFINITIONS,
    DEFAULT_EXPLANATIONS,
    DEFAULT_TEMPLATE_CHANNELS,
    buildSavePayload,
    createInitialRemediationTypes,
    createRemediationType,
    createStep,
    getDurationSummary,
    getStepRecipientCount,
    isValidRemediationConfig,
    normalizeConfigForState,
} from "./remediationConfigModel";

const USE_MOCK_REMEDIATION_CONFIG = false;

function buildRemediationConfigUrl() {
    return `${API_BASE}/ainstein/in/remediation-config`;
}

async function readJsonResponse(response) {
    const text = await response.text();

    if (!text) {
        return null;
    }

    try {
        return JSON.parse(text);
    } catch {
        throw new Error("Backend did not return valid JSON.");
    }
}

function getBackendErrorMessage(data, fallback) {
    return (
        data?.detail ||
        data?.message ||
        data?.error ||
        data?.errors?.[0]?.message ||
        fallback
    );
}

function readFileAsText(file) {
    return new Promise((resolve, reject) => {
        if (!file) {
            resolve("");
            return;
        }

        const reader = new FileReader();

        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = () =>
            reject(reader.error || new Error("File read failed."));
        reader.readAsText(file);
    });
}

function createFullyCollapsedState(remediationTypes) {
    const collapsedTypeIds = {};
    const collapsedStepIds = {};

    remediationTypes.forEach((remediationType) => {
        collapsedTypeIds[remediationType.id] = true;

        remediationType.steps.forEach((step) => {
            collapsedStepIds[step.id] = true;
        });
    });

    return {
        collapsedTypeIds,
        collapsedStepIds,
    };
}

function preserveCollapsedState({
    previousRemediationTypes,
    nextRemediationTypes,
    previousCollapsedTypeIds,
    previousCollapsedStepIds,
}) {
    const collapsedTypeIds = {};
    const collapsedStepIds = {};

    nextRemediationTypes.forEach((nextType, typeIndex) => {
        const previousType =
            previousRemediationTypes.find(
                (remediationType) => remediationType.name === nextType.name
            ) || previousRemediationTypes[typeIndex];

        collapsedTypeIds[nextType.id] = previousType
            ? Boolean(previousCollapsedTypeIds[previousType.id])
            : false;

        nextType.steps.forEach((nextStep, stepIndex) => {
            const previousStep = previousType?.steps?.[stepIndex];

            collapsedStepIds[nextStep.id] = previousStep
                ? Boolean(previousCollapsedStepIds[previousStep.id])
                : false;
        });
    });

    return {
        collapsedTypeIds,
        collapsedStepIds,
    };
}

function RemediationConfigPage({ searchSpace }) {
    const [definitions, setDefinitions] = useState(DEFAULT_DEFINITIONS);
    const [explanations, setExplanations] = useState(DEFAULT_EXPLANATIONS);
    const [templateChannels, setTemplateChannels] = useState(
        DEFAULT_TEMPLATE_CHANNELS
    );

    const [remediationTypes, setRemediationTypes] = useState(() =>
        createInitialRemediationTypes(DEFAULT_TEMPLATE_CHANNELS)
    );

    const [collapsedTypeIds, setCollapsedTypeIds] = useState({});
    const [collapsedStepIds, setCollapsedStepIds] = useState({});

    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState("");

    const [isSaving, setIsSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState({
        type: "",
        message: "",
    });

    const [templateReadError, setTemplateReadError] = useState("");

    const hasAccess = String(searchSpace || "").toLowerCase() === "in";

    const spaceLabel = String(searchSpace || "current").toUpperCase();
    const windowTitle = `${spaceLabel} - remediation window`;

    const totalSteps = useMemo(() => {
        return remediationTypes.reduce(
            (sum, remediationType) => sum + remediationType.steps.length,
            0
        );
    }, [remediationTypes]);

    const clearStatus = () => {
        setSaveStatus({
            type: "",
            message: "",
        });

        setTemplateReadError("");
    };

    const applyConfigToState = (
        data,
        { collapseAll = false, preserveCurrentCollapseState = false } = {}
    ) => {
        const normalized = normalizeConfigForState(data);

        let nextCollapsedState = {
            collapsedTypeIds: {},
            collapsedStepIds: {},
        };

        if (collapseAll) {
            nextCollapsedState = createFullyCollapsedState(
                normalized.remediationTypes
            );
        } else if (preserveCurrentCollapseState) {
            nextCollapsedState = preserveCollapsedState({
                previousRemediationTypes: remediationTypes,
                nextRemediationTypes: normalized.remediationTypes,
                previousCollapsedTypeIds: collapsedTypeIds,
                previousCollapsedStepIds: collapsedStepIds,
            });
        }

        setDefinitions(normalized.definitions);
        setExplanations(normalized.explanations);
        setTemplateChannels(normalized.templateChannels);
        setRemediationTypes(normalized.remediationTypes);
        setCollapsedTypeIds(nextCollapsedState.collapsedTypeIds);
        setCollapsedStepIds(nextCollapsedState.collapsedStepIds);
    };

    const toggleTypeCollapsed = (typeId) => {
        setCollapsedTypeIds((prev) => ({
            ...prev,
            [typeId]: !prev[typeId],
        }));
    };

    const toggleStepCollapsed = (stepId) => {
        setCollapsedStepIds((prev) => ({
            ...prev,
            [stepId]: !prev[stepId],
        }));
    };

    const loadRemediationConfig = useCallback(async () => {
        if (!hasAccess) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setLoadError("");
        setSaveStatus({ type: "", message: "" });
        setTemplateReadError("");

        if (USE_MOCK_REMEDIATION_CONFIG) {
            setTimeout(() => {
                applyConfigToState(MOCK_REMEDIATION_CONFIG, {
                    collapseAll: true,
                });

                setIsLoading(false);
            }, 300);

            return;
        }

        try {
            const response = await fetchWithAuth(
                buildRemediationConfigUrl(),
                {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                    },
                }
            );

            let data = null;

            try {
                data = await readJsonResponse(response);
            } catch (jsonError) {
                if (response.ok) {
                    throw jsonError;
                }
            }

            if (!response.ok) {
                throw new Error(
                    getBackendErrorMessage(
                        data,
                        `Failed to load remediation config (status ${response.status}).`
                    )
                );
            }

            if (!isValidRemediationConfig(data)) {
                throw new Error(
                    "Invalid remediation config returned by backend."
                );
            }

            applyConfigToState(data, {
                collapseAll: true,
            });
        } catch (error) {
            setLoadError(
                error?.message ||
                "Missing authorization or failed to load remediation config."
            );
        } finally {
            setIsLoading(false);
        }
    }, [hasAccess]);

    useEffect(() => {
        loadRemediationConfig();
    }, [loadRemediationConfig]);

    const updateRemediationType = (typeId, updater) => {
        setRemediationTypes((prev) =>
            prev.map((remediationType) =>
                remediationType.id === typeId
                    ? updater(remediationType)
                    : remediationType
            )
        );

        clearStatus();
    };

    const updateStep = (typeId, stepId, updater) => {
        updateRemediationType(typeId, (remediationType) => ({
            ...remediationType,
            steps: remediationType.steps.map((step) =>
                step.id === stepId ? updater(step) : step
            ),
        }));
    };

    const handleAddRemediationType = () => {
        const newType = createRemediationType({
            templateChannels,
            name: `NEW_REMEDIATION_TYPE_${remediationTypes.length + 1}`,
        });

        setRemediationTypes((prev) => [...prev, newType]);

        setCollapsedTypeIds((prev) => ({
            ...prev,
            [newType.id]: false,
        }));

        clearStatus();
    };

    const handleDeleteRemediationType = (typeId) => {
        setRemediationTypes((prev) =>
            prev.filter((remediationType) => remediationType.id !== typeId)
        );

        setCollapsedTypeIds((prev) => {
            const next = { ...prev };
            delete next[typeId];
            return next;
        });

        clearStatus();
    };

    const handleTypeNameChange = (typeId, nextName) => {
        updateRemediationType(typeId, (remediationType) => ({
            ...remediationType,
            name: nextName,
        }));
    };

    const handleEnableProcessingChange = (typeId) => {
        updateRemediationType(typeId, (remediationType) => ({
            ...remediationType,
            enable_processing: !remediationType.enable_processing,
        }));
    };

    const handleAddStep = (typeId) => {
        const newStep = createStep({
            templateChannels,
        });

        updateRemediationType(typeId, (remediationType) => ({
            ...remediationType,
            steps: [...remediationType.steps, newStep],
        }));

        setCollapsedStepIds((prev) => ({
            ...prev,
            [newStep.id]: false,
        }));
    };

    const handleDeleteStep = (typeId, stepId) => {
        updateRemediationType(typeId, (remediationType) => ({
            ...remediationType,
            steps: remediationType.steps.filter(
                (step) => step.id !== stepId
            ),
        }));

        setCollapsedStepIds((prev) => {
            const next = { ...prev };
            delete next[stepId];
            return next;
        });
    };

    const handleStepFieldChange = (
        typeId,
        stepId,
        fieldName,
        nextValue
    ) => {
        updateStep(typeId, stepId, (step) => ({
            ...step,
            [fieldName]: nextValue,
        }));
    };

    const handleRecipientToggle = (
        typeId,
        stepId,
        channel,
        recipient
    ) => {
        updateStep(typeId, stepId, (step) => {
            const channelState = step.channels[channel];
            const isSelected =
                channelState.recipients.includes(recipient);

            return {
                ...step,
                channels: {
                    ...step.channels,
                    [channel]: {
                        ...channelState,
                        recipients: isSelected
                            ? channelState.recipients.filter(
                                (item) => item !== recipient
                            )
                            : [
                                ...channelState.recipients,
                                recipient,
                            ],
                    },
                },
            };
        });
    };

    const handleStepTemplateContentChange = (
        typeId,
        stepId,
        channel,
        nextContent,
        fileName = ""
    ) => {
        updateStep(typeId, stepId, (step) => ({
            ...step,
            channels: {
                ...step.channels,
                [channel]: {
                    ...step.channels[channel],
                    mail_template: {
                        content: nextContent,
                        fileName,
                    },
                },
            },
        }));
    };

    const handleCaseClosedTemplateContentChange = (
        typeId,
        closureKey,
        nextContent,
        fileName = ""
    ) => {
        updateRemediationType(typeId, (remediationType) => ({
            ...remediationType,
            mail_templates_case_closed: {
                ...remediationType.mail_templates_case_closed,
                [closureKey]: {
                    content: nextContent,
                    fileName,
                },
            },
        }));
    };

    const handleStepTemplateUpload = async (
        typeId,
        stepId,
        channel,
        file
    ) => {
        if (!file) return;

        try {
            const content = await readFileAsText(file);

            handleStepTemplateContentChange(
                typeId,
                stepId,
                channel,
                content,
                file.name
            );
        } catch (error) {
            setTemplateReadError(
                error?.message ||
                "Could not read selected template file."
            );
        }
    };

    const handleCaseClosedTemplateUpload = async (
        typeId,
        closureKey,
        file
    ) => {
        if (!file) return;

        try {
            const content = await readFileAsText(file);

            handleCaseClosedTemplateContentChange(
                typeId,
                closureKey,
                content,
                file.name
            );
        } catch (error) {
            setTemplateReadError(
                error?.message ||
                "Could not read selected template file."
            );
        }
    };

    const handleSave = async () => {
        const payload = buildSavePayload({
            definitions,
            explanations,
            templateChannels,
            remediationTypes,
        });

        console.log(
            USE_MOCK_REMEDIATION_CONFIG
                ? "Mock remediation config payload:"
                : "Remediation config payload:"
        );

        console.log(JSON.stringify(payload, null, 2));

        setIsSaving(true);
        setSaveStatus({ type: "", message: "" });

        if (USE_MOCK_REMEDIATION_CONFIG) {
            setTimeout(() => {
                setIsSaving(false);
                setSaveStatus({
                    type: "success",
                    message:
                        "Mock save successful. Payload was logged in the browser console.",
                });
            }, 500);

            return;
        }

        try {
            const response = await fetchWithAuth(
                buildRemediationConfigUrl(),
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                    body: JSON.stringify(payload),
                }
            );

            let returnedData = null;

            try {
                returnedData = await readJsonResponse(response);
            } catch (jsonError) {
                if (response.ok) {
                    returnedData = null;
                } else {
                    throw jsonError;
                }
            }

            if (!response.ok) {
                throw new Error(
                    getBackendErrorMessage(
                        returnedData,
                        `Failed to save remediation config (status ${response.status}).`
                    )
                );
            }

            if (isValidRemediationConfig(returnedData)) {
                applyConfigToState(returnedData, {
                    preserveCurrentCollapseState: true,
                });
            }

            setSaveStatus({
                type: "success",
                message: "Remediation config saved successfully.",
            });
        } catch (error) {
            setSaveStatus({
                type: "error",
                message:
                    error?.message ||
                    "An unexpected error occurred while saving remediation config.",
            });
        } finally {
            setIsSaving(false);
        }
    };

    const renderTemplateCard = ({
        inputId,
        label,
        template,
        onUpload,
        onContentChange,
    }) => (
        <div
            key={inputId}
            className="remediation-config-template-card"
        >
            <div className="remediation-config-template-card-header">
                <label
                    className="remediation-config-template-label"
                    htmlFor={inputId}
                >
                    {label}
                </label>

                <label
                    className={`remediation-config-secondary-button remediation-config-template-button ${isSaving ? "is-disabled" : ""
                        }`}
                    htmlFor={isSaving ? undefined : inputId}
                >
                    Upload
                </label>

                <input
                    id={inputId}
                    className="remediation-config-file-input"
                    type="file"
                    accept=".html,.htm,.txt,.md"
                    disabled={isSaving}
                    onChange={(event) => {
                        const file =
                            event.target.files?.[0] || null;

                        onUpload(file);
                        event.target.value = "";
                    }}
                />
            </div>

            {template.fileName && (
                <div className="remediation-config-template-file-name">
                    {template.fileName}
                </div>
            )}

            <textarea
                className="remediation-config-template-textarea"
                value={template.content}
                onChange={(event) =>
                    onContentChange(
                        event.target.value,
                        template.fileName
                    )
                }
                placeholder="<html></html>"
                rows={6}
                disabled={isSaving}
            />
        </div>
    );

    return (
        <div className="remediation-config-shell">
            <div className="remediation-config-main">
                <div className="remediation-config-inner">
                    <div className="remediation-config-header-row">
                        <div>
                            <h2 className="remediation-config-title">
                                Remediation Config
                            </h2>

                            <p className="remediation-config-page-subtitle">
                                Configure remediation types, recipients,
                                durations, and mail templates.
                            </p>
                        </div>
                    </div>

                    {hasAccess ? (
                        <div className="remediation-config-card">
                            <div className="remediation-config-window-header">
                                <div>
                                    <h3 className="remediation-config-window-title">
                                        {windowTitle}
                                    </h3>

                                    <p className="remediation-config-window-subtitle">
                                        {remediationTypes.length} remediation
                                        type
                                        {remediationTypes.length === 1
                                            ? ""
                                            : "s"}{" "}
                                        · {totalSteps} step
                                        {totalSteps === 1 ? "" : "s"}
                                    </p>
                                </div>
                            </div>

                            {isLoading ? (
                                <div className="remediation-config-feedback remediation-config-feedback--info">
                                    {USE_MOCK_REMEDIATION_CONFIG
                                        ? "Loading mock remediation config..."
                                        : "Loading remediation config..."}
                                </div>
                            ) : loadError ? (
                                <>
                                    <div className="remediation-config-feedback remediation-config-feedback--error">
                                        {loadError}
                                    </div>

                                    <div className="remediation-config-loading-actions">
                                        <button
                                            type="button"
                                            className="remediation-config-secondary-button"
                                            onClick={
                                                loadRemediationConfig
                                            }
                                        >
                                            Retry loading
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    {USE_MOCK_REMEDIATION_CONFIG && (
                                        <div className="remediation-config-feedback remediation-config-feedback--info">
                                            Mock mode active. Backend calls
                                            are currently disabled.
                                        </div>
                                    )}

                                    {saveStatus.message && (
                                        <div
                                            className={`remediation-config-feedback ${saveStatus.type ===
                                                    "success"
                                                    ? "remediation-config-feedback--success"
                                                    : "remediation-config-feedback--error"
                                                }`}
                                        >
                                            {saveStatus.message}
                                        </div>
                                    )}

                                    {templateReadError && (
                                        <div className="remediation-config-feedback remediation-config-feedback--error">
                                            {templateReadError}
                                        </div>
                                    )}

                                    {remediationTypes.length === 0 ? (
                                        <div className="remediation-config-empty">
                                            No remediation types configured.
                                            Add a remediation type to start.
                                        </div>
                                    ) : (
                                        <div className="remediation-config-types-list">
                                            {remediationTypes.map(
                                                (
                                                    remediationType,
                                                    typeIndex
                                                ) => {
                                                    const isTypeCollapsed =
                                                        Boolean(
                                                            collapsedTypeIds[
                                                            remediationType
                                                                .id
                                                            ]
                                                        );

                                                    const typeStepCount =
                                                        remediationType.steps
                                                            .length;

                                                    return (
                                                        <section
                                                            key={
                                                                remediationType.id
                                                            }
                                                            className={`remediation-config-type-card ${isTypeCollapsed
                                                                    ? "is-collapsed"
                                                                    : ""
                                                                }`}
                                                        >
                                                            <div className="remediation-config-type-header">
                                                                <div className="remediation-config-type-heading">
                                                                    <label
                                                                        className="remediation-config-type-label"
                                                                        htmlFor={`remediation-type-${remediationType.id}`}
                                                                    >
                                                                        Remediation
                                                                        Type{" "}
                                                                        {typeIndex +
                                                                            1}
                                                                    </label>

                                                                    <input
                                                                        id={`remediation-type-${remediationType.id}`}
                                                                        className="remediation-config-type-name-input"
                                                                        type="text"
                                                                        value={
                                                                            remediationType.name
                                                                        }
                                                                        onChange={(
                                                                            event
                                                                        ) =>
                                                                            handleTypeNameChange(
                                                                                remediationType.id,
                                                                                event
                                                                                    .target
                                                                                    .value
                                                                            )
                                                                        }
                                                                        placeholder="Enter remediation type name"
                                                                        disabled={
                                                                            isSaving
                                                                        }
                                                                    />

                                                                    <div className="remediation-config-type-summary">
                                                                        <span
                                                                            className={`remediation-config-status-pill ${remediationType.enable_processing
                                                                                    ? "is-enabled"
                                                                                    : "is-disabled"
                                                                                }`}
                                                                        >
                                                                            {remediationType.enable_processing
                                                                                ? "Enabled"
                                                                                : "Disabled"}
                                                                        </span>

                                                                        <span>
                                                                            {
                                                                                typeStepCount
                                                                            }{" "}
                                                                            step
                                                                            {typeStepCount ===
                                                                                1
                                                                                ? ""
                                                                                : "s"}
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                <div className="remediation-config-header-actions">
                                                                    <button
                                                                        type="button"
                                                                        className="remediation-config-collapse-button"
                                                                        onClick={() =>
                                                                            toggleTypeCollapsed(
                                                                                remediationType.id
                                                                            )
                                                                        }
                                                                        title={
                                                                            isTypeCollapsed
                                                                                ? "Expand remediation type"
                                                                                : "Collapse remediation type"
                                                                        }
                                                                        aria-label={
                                                                            isTypeCollapsed
                                                                                ? "Expand remediation type"
                                                                                : "Collapse remediation type"
                                                                        }
                                                                        aria-expanded={
                                                                            !isTypeCollapsed
                                                                        }
                                                                    >
                                                                        {isTypeCollapsed ? (
                                                                            <ArrowheadDown
                                                                                width={
                                                                                    18
                                                                                }
                                                                                height={
                                                                                    18
                                                                                }
                                                                                aria-hidden="true"
                                                                            />
                                                                        ) : (
                                                                            <ArrowheadUp
                                                                                width={
                                                                                    18
                                                                                }
                                                                                height={
                                                                                    18
                                                                                }
                                                                                aria-hidden="true"
                                                                            />
                                                                        )}
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        className="remediation-config-delete-button"
                                                                        onClick={() =>
                                                                            handleDeleteRemediationType(
                                                                                remediationType.id
                                                                            )
                                                                        }
                                                                        title="Delete remediation type"
                                                                        aria-label="Delete remediation type"
                                                                        disabled={
                                                                            isSaving
                                                                        }
                                                                    >
                                                                        <Trash
                                                                            width={
                                                                                18
                                                                            }
                                                                            height={
                                                                                18
                                                                            }
                                                                            stroke="currentColor"
                                                                            fill="none"
                                                                            strokeWidth="2"
                                                                            aria-hidden="true"
                                                                        />
                                                                    </button>
                                                                </div>
                                                            </div>

                                                            {!isTypeCollapsed && (
                                                                <div className="remediation-config-type-body">
                                                                    <div className="remediation-config-field-row remediation-config-field-row--toggle">
                                                                        <div className="remediation-config-field-text">
                                                                            <span className="remediation-config-field-label">
                                                                                Enable
                                                                                processing
                                                                            </span>

                                                                            <p className="remediation-config-field-help">
                                                                                {
                                                                                    explanations.enable_processing
                                                                                }
                                                                            </p>
                                                                        </div>

                                                                        <button
                                                                            type="button"
                                                                            className={`remediation-config-toggle ${remediationType.enable_processing
                                                                                    ? "is-on"
                                                                                    : ""
                                                                                }`}
                                                                            onClick={() =>
                                                                                handleEnableProcessingChange(
                                                                                    remediationType.id
                                                                                )
                                                                            }
                                                                            aria-pressed={
                                                                                remediationType.enable_processing
                                                                            }
                                                                            disabled={
                                                                                isSaving
                                                                            }
                                                                        >
                                                                            <span className="remediation-config-toggle-thumb" />
                                                                        </button>
                                                                    </div>

                                                                    <div className="remediation-config-section-block">
                                                                        <div className="remediation-config-section-header">
                                                                            <div>
                                                                                <h4 className="remediation-config-section-title">
                                                                                    Steps
                                                                                </h4>

                                                                                <p className="remediation-config-section-help">
                                                                                    {
                                                                                        explanations.step_number
                                                                                    }
                                                                                </p>
                                                                            </div>
                                                                        </div>

                                                                        {remediationType
                                                                            .steps
                                                                            .length ===
                                                                            0 ? (
                                                                            <div className="remediation-config-step-empty">
                                                                                No
                                                                                steps
                                                                                configured
                                                                                for
                                                                                this
                                                                                remediation
                                                                                type.
                                                                            </div>
                                                                        ) : (
                                                                            <div className="remediation-config-steps-list">
                                                                                {remediationType.steps.map(
                                                                                    (
                                                                                        step,
                                                                                        stepIndex
                                                                                    ) => {
                                                                                        const durationId = `duration-${step.id}`;

                                                                                        const isStepCollapsed =
                                                                                            Boolean(
                                                                                                collapsedStepIds[
                                                                                                step
                                                                                                    .id
                                                                                                ]
                                                                                            );

                                                                                        const recipientCount =
                                                                                            getStepRecipientCount(
                                                                                                step
                                                                                            );

                                                                                        const durationSummary =
                                                                                            getDurationSummary(
                                                                                                step.maximum_step_duration
                                                                                            );

                                                                                        return (
                                                                                            <section
                                                                                                key={
                                                                                                    step.id
                                                                                                }
                                                                                                className={`remediation-config-step-card ${isStepCollapsed
                                                                                                        ? "is-collapsed"
                                                                                                        : ""
                                                                                                    }`}
                                                                                            >
                                                                                                <div className="remediation-config-step-header">
                                                                                                    <div>
                                                                                                        <h4 className="remediation-config-step-title">
                                                                                                            Step{" "}
                                                                                                            {stepIndex +
                                                                                                                1}
                                                                                                        </h4>

                                                                                                        <div className="remediation-config-step-summary">
                                                                                                            <span>
                                                                                                                {
                                                                                                                    recipientCount
                                                                                                                }{" "}
                                                                                                                recipient
                                                                                                                {recipientCount ===
                                                                                                                    1
                                                                                                                    ? ""
                                                                                                                    : "s"}
                                                                                                            </span>

                                                                                                            <span>
                                                                                                                {
                                                                                                                    durationSummary
                                                                                                                }
                                                                                                            </span>
                                                                                                        </div>

                                                                                                        {!isStepCollapsed && (
                                                                                                            <p className="remediation-config-step-subtitle">
                                                                                                                Step
                                                                                                                number
                                                                                                                is
                                                                                                                generated
                                                                                                                automatically
                                                                                                                on
                                                                                                                save.
                                                                                                            </p>
                                                                                                        )}
                                                                                                    </div>

                                                                                                    <div className="remediation-config-header-actions">
                                                                                                        <button
                                                                                                            type="button"
                                                                                                            className="remediation-config-collapse-button remediation-config-collapse-button--compact"
                                                                                                            onClick={() =>
                                                                                                                toggleStepCollapsed(
                                                                                                                    step.id
                                                                                                                )
                                                                                                            }
                                                                                                            title={
                                                                                                                isStepCollapsed
                                                                                                                    ? "Expand step"
                                                                                                                    : "Collapse step"
                                                                                                            }
                                                                                                            aria-label={
                                                                                                                isStepCollapsed
                                                                                                                    ? "Expand step"
                                                                                                                    : "Collapse step"
                                                                                                            }
                                                                                                            aria-expanded={
                                                                                                                !isStepCollapsed
                                                                                                            }
                                                                                                        >
                                                                                                            {isStepCollapsed ? (
                                                                                                                <ArrowheadDown
                                                                                                                    width={
                                                                                                                        16
                                                                                                                    }
                                                                                                                    height={
                                                                                                                        16
                                                                                                                    }
                                                                                                                    aria-hidden="true"
                                                                                                                />
                                                                                                            ) : (
                                                                                                                <ArrowheadUp
                                                                                                                    width={
                                                                                                                        16
                                                                                                                    }
                                                                                                                    height={
                                                                                                                        16
                                                                                                                    }
                                                                                                                    aria-hidden="true"
                                                                                                                />
                                                                                                            )}
                                                                                                        </button>

                                                                                                        <button
                                                                                                            type="button"
                                                                                                            className="remediation-config-delete-button remediation-config-delete-button--compact"
                                                                                                            onClick={() =>
                                                                                                                handleDeleteStep(
                                                                                                                    remediationType.id,
                                                                                                                    step.id
                                                                                                                )
                                                                                                            }
                                                                                                            title="Delete step"
                                                                                                            aria-label="Delete step"
                                                                                                            disabled={
                                                                                                                isSaving
                                                                                                            }
                                                                                                        >
                                                                                                            <Trash
                                                                                                                width={
                                                                                                                    16
                                                                                                                }
                                                                                                                height={
                                                                                                                    16
                                                                                                                }
                                                                                                                stroke="currentColor"
                                                                                                                fill="none"
                                                                                                                strokeWidth="2"
                                                                                                                aria-hidden="true"
                                                                                                            />
                                                                                                        </button>
                                                                                                    </div>
                                                                                                </div>

                                                                                                {!isStepCollapsed && (
                                                                                                    <div className="remediation-config-step-body">
                                                                                                        <div className="remediation-config-step-fields">
                                                                                                            <div className="remediation-config-field-row">
                                                                                                                <div className="remediation-config-field-text">
                                                                                                                    <label
                                                                                                                        className="remediation-config-field-label"
                                                                                                                        htmlFor={
                                                                                                                            durationId
                                                                                                                        }
                                                                                                                    >
                                                                                                                        Maximum
                                                                                                                        step
                                                                                                                        duration
                                                                                                                    </label>

                                                                                                                    <p className="remediation-config-field-help">
                                                                                                                        {
                                                                                                                            explanations.maximum_step_duration
                                                                                                                        }
                                                                                                                    </p>
                                                                                                                </div>

                                                                                                                <input
                                                                                                                    id={
                                                                                                                        durationId
                                                                                                                    }
                                                                                                                    className="remediation-config-input"
                                                                                                                    type="number"
                                                                                                                    min="0"
                                                                                                                    value={
                                                                                                                        step.maximum_step_duration
                                                                                                                    }
                                                                                                                    onChange={(
                                                                                                                        event
                                                                                                                    ) =>
                                                                                                                        handleStepFieldChange(
                                                                                                                            remediationType.id,
                                                                                                                            step.id,
                                                                                                                            "maximum_step_duration",
                                                                                                                            event
                                                                                                                                .target
                                                                                                                                .value
                                                                                                                        )
                                                                                                                    }
                                                                                                                    placeholder="e.g. 7"
                                                                                                                    disabled={
                                                                                                                        isSaving
                                                                                                                    }
                                                                                                                />
                                                                                                            </div>

                                                                                                            <div className="remediation-config-channel-list">
                                                                                                                {templateChannels.map(
                                                                                                                    (
                                                                                                                        channel
                                                                                                                    ) => {
                                                                                                                        const channelState =
                                                                                                                            step
                                                                                                                                .channels[
                                                                                                                            channel
                                                                                                                            ];

                                                                                                                        const stepTemplateId = `step-template-${step.id}-${channel}`;

                                                                                                                        return (
                                                                                                                            <div
                                                                                                                                key={
                                                                                                                                    channel
                                                                                                                                }
                                                                                                                                className="remediation-config-channel-block"
                                                                                                                            >
                                                                                                                                <h5 className="remediation-config-channel-title">
                                                                                                                                    {
                                                                                                                                        channel
                                                                                                                                    }
                                                                                                                                </h5>

                                                                                                                                <div className="remediation-config-field-row remediation-config-field-row--top">
                                                                                                                                    <div className="remediation-config-field-text">
                                                                                                                                        <span className="remediation-config-field-label">
                                                                                                                                            Recipients
                                                                                                                                        </span>

                                                                                                                                        <p className="remediation-config-field-help">
                                                                                                                                            {
                                                                                                                                                explanations.recipients
                                                                                                                                            }
                                                                                                                                        </p>
                                                                                                                                    </div>

                                                                                                                                    <div className="remediation-config-recipient-grid">
                                                                                                                                        {definitions.possible_recipients.map(
                                                                                                                                            (
                                                                                                                                                recipient
                                                                                                                                            ) => {
                                                                                                                                                const isSelected =
                                                                                                                                                    channelState.recipients.includes(
                                                                                                                                                        recipient
                                                                                                                                                    );

                                                                                                                                                return (
                                                                                                                                                    <label
                                                                                                                                                        key={
                                                                                                                                                            recipient
                                                                                                                                                        }
                                                                                                                                                        className={`remediation-config-recipient-chip ${isSelected
                                                                                                                                                                ? "is-selected"
                                                                                                                                                                : ""
                                                                                                                                                            }`}
                                                                                                                                                    >
                                                                                                                                                        <input
                                                                                                                                                            type="checkbox"
                                                                                                                                                            checked={
                                                                                                                                                                isSelected
                                                                                                                                                            }
                                                                                                                                                            disabled={
                                                                                                                                                                isSaving
                                                                                                                                                            }
                                                                                                                                                            onChange={() =>
                                                                                                                                                                handleRecipientToggle(
                                                                                                                                                                    remediationType.id,
                                                                                                                                                                    step.id,
                                                                                                                                                                    channel,
                                                                                                                                                                    recipient
                                                                                                                                                                )
                                                                                                                                                            }
                                                                                                                                                        />

                                                                                                                                                        <span>
                                                                                                                                                            {
                                                                                                                                                                recipient
                                                                                                                                                            }
                                                                                                                                                        </span>
                                                                                                                                                    </label>
                                                                                                                                                );
                                                                                                                                            }
                                                                                                                                        )}
                                                                                                                                    </div>
                                                                                                                                </div>

                                                                                                                                <div className="remediation-config-template-section">
                                                                                                                                    <div className="remediation-config-template-section-header">
                                                                                                                                        <h5 className="remediation-config-template-section-title">
                                                                                                                                            Mail
                                                                                                                                            template
                                                                                                                                        </h5>

                                                                                                                                        <p className="remediation-config-field-help">
                                                                                                                                            {
                                                                                                                                                explanations.mail_templates
                                                                                                                                            }
                                                                                                                                        </p>
                                                                                                                                    </div>

                                                                                                                                    <div className="remediation-config-template-grid remediation-config-template-grid--single">
                                                                                                                                        {renderTemplateCard(
                                                                                                                                            {
                                                                                                                                                inputId:
                                                                                                                                                    stepTemplateId,
                                                                                                                                                label: channel,
                                                                                                                                                template:
                                                                                                                                                    channelState.mail_template,
                                                                                                                                                onUpload:
                                                                                                                                                    (
                                                                                                                                                        file
                                                                                                                                                    ) =>
                                                                                                                                                        handleStepTemplateUpload(
                                                                                                                                                            remediationType.id,
                                                                                                                                                            step.id,
                                                                                                                                                            channel,
                                                                                                                                                            file
                                                                                                                                                        ),
                                                                                                                                                onContentChange:
                                                                                                                                                    (
                                                                                                                                                        nextContent,
                                                                                                                                                        fileName
                                                                                                                                                    ) =>
                                                                                                                                                        handleStepTemplateContentChange(
                                                                                                                                                            remediationType.id,
                                                                                                                                                            step.id,
                                                                                                                                                            channel,
                                                                                                                                                            nextContent,
                                                                                                                                                            fileName
                                                                                                                                                        ),
                                                                                                                                            }
                                                                                                                                        )}
                                                                                                                                    </div>
                                                                                                                                </div>
                                                                                                                            </div>
                                                                                                                        );
                                                                                                                    }
                                                                                                                )}
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                )}
                                                                                            </section>
                                                                                        );
                                                                                    }
                                                                                )}
                                                                            </div>
                                                                        )}

                                                                        <div className="remediation-config-type-footer">
                                                                            <button
                                                                                type="button"
                                                                                className="remediation-config-secondary-button"
                                                                                onClick={() =>
                                                                                    handleAddStep(
                                                                                        remediationType.id
                                                                                    )
                                                                                }
                                                                                disabled={
                                                                                    isSaving
                                                                                }
                                                                            >
                                                                                +
                                                                                Add
                                                                                step
                                                                            </button>
                                                                        </div>
                                                                    </div>

                                                                    <div className="remediation-config-template-section remediation-config-template-section--case-closed">
                                                                        <div className="remediation-config-template-section-header">
                                                                            <h4 className="remediation-config-section-title">
                                                                                Case
                                                                                closed
                                                                                mail
                                                                                templates
                                                                            </h4>

                                                                            <p className="remediation-config-field-help">
                                                                                {
                                                                                    explanations.mail_templates_case_closed
                                                                                }
                                                                            </p>
                                                                        </div>

                                                                        <div className="remediation-config-template-grid">
                                                                            {CASE_CLOSED_TYPES.map(
                                                                                (
                                                                                    closureType
                                                                                ) =>
                                                                                    renderTemplateCard(
                                                                                        {
                                                                                            inputId: `case-closed-template-${remediationType.id}-${closureType.key}`,
                                                                                            label: closureType.label,
                                                                                            template:
                                                                                                remediationType
                                                                                                    .mail_templates_case_closed[
                                                                                                closureType
                                                                                                    .key
                                                                                                ],
                                                                                            onUpload:
                                                                                                (
                                                                                                    file
                                                                                                ) =>
                                                                                                    handleCaseClosedTemplateUpload(
                                                                                                        remediationType.id,
                                                                                                        closureType.key,
                                                                                                        file
                                                                                                    ),
                                                                                            onContentChange:
                                                                                                (
                                                                                                    nextContent,
                                                                                                    fileName
                                                                                                ) =>
                                                                                                    handleCaseClosedTemplateContentChange(
                                                                                                        remediationType.id,
                                                                                                        closureType.key,
                                                                                                        nextContent,
                                                                                                        fileName
                                                                                                    ),
                                                                                        }
                                                                                    )
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </section>
                                                    );
                                                }
                                            )}
                                        </div>
                                    )}

                                    <div className="remediation-config-footer">
                                        <button
                                            type="button"
                                            className="remediation-config-secondary-button"
                                            onClick={
                                                handleAddRemediationType
                                            }
                                            disabled={isSaving}
                                        >
                                            + Add remediation type
                                        </button>

                                        <button
                                            type="button"
                                            className="remediation-config-save-button"
                                            onClick={handleSave}
                                            disabled={isSaving}
                                        >
                                            {isSaving
                                                ? "Saving..."
                                                : "Save"}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="remediation-config-card remediation-config-card--spaced">
                            <div className="remediation-config-empty">
                                Remediation config is not available for this
                                space.
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default RemediationConfigPage;